import ILocalContentService from 'src/system/content/services/ILocalContentService'
import IRemoteContentService from 'src/system/content/services/IRemoteContentService'
import IQuoteService from 'src/system/content/services/IQuoteService'
import { AxiosResponse } from 'axios'
import { IQuoteApiResponse } from 'src/system/content/api/IQuoteApiResponse'
import { QuoteDocType, QuoteDocument } from 'src/system/content/schemas/quote'
import { IQuoteApiRequest } from 'src/system/content/api/IQuoteApiRequest'

export class QuoteService implements IQuoteService {
  constructor(private localContentService: ILocalContentService, private remoteContentService: IRemoteContentService) {
    //
  }

  async fetchAllQuotes(): Promise<void> {
    const response: AxiosResponse<IQuoteApiResponse[]> = await this.remoteContentService.request('GET', '/api/quote')

    const resources = new Map<string, object>()

    const quotes: QuoteDocType[] = response.data.map((quote) => {
      quote.resources.forEach((resource) => {
        resources.set(`${resource.type}-${resource.id}`, {
          resourceId: resource.id.toString(),
          type: resource.type,
          folders: [],
          name: resource.name,
          description: resource.description,
          thumbnail: resource.thumbnail,
          image: resource.image,
          document: resource.document,
          url: resource.url,
          video: resource.video,
          fileSize: resource.file_size,
          createdAt: resource.created_at,
          updatedAt: resource.updated_at,
        })
      })

      return {
        id: quote.id.toString(),
        title: quote.title,
        introduction: quote.introduction,
        validUntil: quote.valid_until,
        resources: quote.resources?.map((resource) => `${resource.type}-${resource.id}`) || [],
        data: quote.data,
        value: quote.value,
        createdAt: quote.created_at,
        updatedAt: quote.updated_at,
      }
    })

    const db = this.localContentService.getDatabase()

    const storedIds = (await db.quotes.find().exec()).map((quote) => quote.id)
    const responseIds = quotes.map((quote) => quote.id)

    const idsToDelete = storedIds.filter((id) => !responseIds.includes(id))

    await Promise.all([
      db.quotes.bulkRemove(idsToDelete),
      db.quotes.bulkUpsert(quotes),
      db.resources.bulkUpsert(Array.from(resources.values())),
    ])
  }

  async saveQuote(quote: IQuoteApiRequest): Promise<QuoteDocument> {
    const response: AxiosResponse<IQuoteApiResponse> = await this.remoteContentService.request(
      'POST',
      '/api/quote/save',
      quote
    )

    const quoteData = {
      id: response.data.id.toString(),
      title: response.data.title,
      introduction: response.data.introduction,
      validUntil: response.data.valid_until,
      resources: response.data.resources?.map((resource) => `${resource.type}-${resource.id}`) || [],
      data: response.data.data,
      value: response.data.value,
      createdAt: response.data.created_at,
      updatedAt: response.data.updated_at,
    }

    const db = this.localContentService.getDatabase()

    return await db.quotes.upsert(quoteData)
  }
}
